import { useState, useEffect, useCallback, useRef } from 'react'
import { Socket, Channel } from 'phoenix'
import { useAuth } from '../context/AuthContext'
import { api } from '../services/api'

export interface Notification {
  id: string
  type: string
  title: string
  message: string
  reference_id: string | null
  read: boolean
  created_at: string
}

interface UseNotificationsOptions {
  onNewNotification?: (notification: Notification) => void
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { user } = useAuth()
  const token = '' // TODO: implement token from auth
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [connected, setConnected] = useState(false)
  const [loading, setLoading] = useState(true)

  const socketRef = useRef<Socket | null>(null)
  const channelRef = useRef<Channel | null>(null)

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications')
      setNotifications(response.data.notifications || [])
      setUnreadCount(response.data.unreadCount || 0)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`)
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }, [])

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }, [])

  // Connect to Phoenix WebSocket
  useEffect(() => {
    if (!user || !token) return

    const phoenixUrl = import.meta.env.VITE_REALTIME_URL || 'ws://localhost:4001/socket'

    // Create socket connection
    const socket = new Socket(phoenixUrl, {
      params: { token }
    })

    socket.onOpen(() => {
      console.log('Phoenix socket connected')
      setConnected(true)
    })

    socket.onClose(() => {
      console.log('Phoenix socket disconnected')
      setConnected(false)
    })

    socket.onError((error) => {
      console.error('Phoenix socket error:', error)
      setConnected(false)
    })

    socket.connect()
    socketRef.current = socket

    // Join user's notification channel
    const channel = socket.channel(`notifications:${user.id}`, {})

    channel.join()
      .receive('ok', () => {
        console.log(`Joined notifications channel for user ${user.id}`)
      })
      .receive('error', (resp) => {
        console.error('Failed to join notifications channel:', resp)
      })

    // Listen for new notifications
    channel.on('new_notification', (payload: Notification) => {
      console.log('New notification received:', payload)

      setNotifications(prev => [payload, ...prev])
      setUnreadCount(prev => prev + 1)

      // Call the callback if provided
      if (options.onNewNotification) {
        options.onNewNotification(payload)
      }
    })

    channelRef.current = channel

    // Fetch initial notifications
    fetchNotifications()

    // Cleanup
    return () => {
      if (channelRef.current) {
        channelRef.current.leave()
      }
      if (socketRef.current) {
        socketRef.current.disconnect()
      }
    }
  }, [user, token, fetchNotifications, options.onNewNotification])

  return {
    notifications,
    unreadCount,
    connected,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications
  }
}
